import * as THREE from 'three';

/**
 * Projection of a 3D direction onto the vertical plane that faces the viewer at an
 * impact point. The plane's normal is the *horizontal* viewer→point direction and
 * its vertical is world up, so the result is independent of where on the target the
 * blade actually landed — it describes the swing, not the contact surface.
 */
export type FrontalProjection = {
  /** 0° = straight down (fendente), 90° = viewer's right (tondo), 180° = up (montante); clockwise, [0,360). */
  angleDeg: number;
  /** |in-plane component| / |input dir|. 1 = fully in the plane, 0 = straight toward/away from viewer. */
  ratio: number;
  /**
   * Rotation (rad) about +Z to align a +X-long bar with the projected line, in the
   * billboard basis (local X = viewer's right, local Y = world up, local Z = toward viewer).
   */
  barRad: number;
};

const _n = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _right = new THREE.Vector3();
const _inPlane = new THREE.Vector3();

/**
 * Build the orthonormal billboard basis at `point` as seen from `viewerPos`:
 * X = viewer's right, Y = world up, Z = horizontal direction toward the viewer.
 * Falls back to world +Z if the viewer is directly above the point.
 */
export function frontalBasis(
  point: THREE.Vector3,
  viewerPos: THREE.Vector3,
  outRight: THREE.Vector3,
  outUp: THREE.Vector3,
  outNormal: THREE.Vector3,
): void {
  outNormal.set(viewerPos.x - point.x, 0, viewerPos.z - point.z);
  if (outNormal.lengthSq() < 1e-9) outNormal.set(0, 0, 1);
  outNormal.normalize();
  outUp.set(0, 1, 0);
  outRight.crossVectors(outUp, outNormal).normalize(); // right = up × n (right-handed with Z = n)
}

/**
 * Project `dir` onto the viewer-facing vertical plane at `point`. Returns null if
 * `dir` is not finite or has no length.
 */
export function projectToFrontalPlane(
  dir: THREE.Vector3,
  point: THREE.Vector3,
  viewerPos: THREE.Vector3,
): FrontalProjection | null {
  if (!isFinite(dir.x) || !isFinite(dir.y) || !isFinite(dir.z)) return null;
  const dirLen = dir.length();
  if (dirLen < 1e-6) return null;

  frontalBasis(point, viewerPos, _right, _up, _n);

  _inPlane.copy(dir).addScaledVector(_n, -dir.dot(_n));
  const inLen = _inPlane.length();
  const ratio = inLen / dirLen;
  if (inLen < 1e-6) return { angleDeg: 0, ratio: 0, barRad: 0 };

  const vUp = _inPlane.dot(_up);
  const vRight = _inPlane.dot(_right);

  // 0 = down (−up), 90 = right, clockwise as seen by the viewer.
  let deg = Math.atan2(vRight, -vUp) * (180 / Math.PI);
  if (deg < 0) deg += 360;

  // Bar's +X (local frame: X = right, Y = up) aligns with the projected (vRight, vUp) line.
  const barRad = Math.atan2(vUp, vRight);

  return { angleDeg: deg, ratio, barRad };
}
